import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Grid,
  Chip,
  Avatar,
  IconButton,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faTag,
  faCalendarAlt,
  faClock,
  faCheck,
  faCoins,
  faRotate,
  faCreditCard,
} from '@fortawesome/free-solid-svg-icons'
import {
  faCircleCheck,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import OutlineButton from '../buttons/OutlineButton'
import GradientButton from '../buttons/GradientButton'

const BRAND_GRADIENT = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'

const StatCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: '12px',
  backgroundColor: '#ffffff',
  border: '1px solid rgba(102, 126, 234, 0.12)',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
  height: '100%',
  transition: 'box-shadow 0.2s ease, transform 0.2s ease',
  '@media (hover: hover)': {
    '&:hover': {
      boxShadow: '0 4px 14px rgba(102, 126, 234, 0.12)',
      transform: 'translateY(-1px)',
    },
  },
}))

const SectionCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: '14px',
  backgroundColor: '#ffffff',
  border: '1px solid rgba(102, 126, 234, 0.12)',
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.04)',
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(3),
  },
}))

const StatusChip = styled(Chip)(({ statuscolor }) => ({
  fontWeight: 700,
  fontSize: '0.8125rem',
  borderRadius: '8px',
  ...(statuscolor === 'success' && {
    backgroundColor: 'rgba(76, 175, 80, 0.12)',
    color: '#2e7d32',
  }),
  ...(statuscolor === 'error' && {
    backgroundColor: 'rgba(244, 67, 54, 0.12)',
    color: '#c62828',
  }),
  ...(statuscolor === 'default' && {
    backgroundColor: 'rgba(255, 152, 0, 0.12)',
    color: '#e65100',
  }),
}))

const FeatureItem = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 12,
  padding: '10px 12px',
  borderRadius: '10px',
  backgroundColor: 'rgba(102, 126, 234, 0.04)',
  border: '1px solid rgba(102, 126, 234, 0.08)',
})

const InfoRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  padding: theme.spacing(1.5),
  borderRadius: '10px',
  backgroundColor: 'rgba(249, 250, 251, 0.9)',
  border: '1px solid rgba(229, 231, 235, 0.9)',
  height: '100%',
}))

const StatIcon = ({ icon }) => (
  <Box
    sx={{
      width: 36,
      height: 36,
      borderRadius: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      background:
        'linear-gradient(135deg, rgba(102, 126, 234, 0.14), rgba(118, 75, 162, 0.14))',
      color: '#667eea',
    }}
  >
    <FontAwesomeIcon icon={icon} fontSize={14} />
  </Box>
)

const DetailSubscription = ({ selectedOffre, setSelectedOffre }) => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'))

  if (!selectedOffre) {
    return null
  }

  const planName =
    i18n.language === 'fr'
      ? selectedOffre?.subscriptionPlan?.name
      : selectedOffre?.subscriptionPlan?.nameEn ||
        selectedOffre?.subscriptionPlan?.name

  const planDescription =
    i18n.language === 'fr'
      ? selectedOffre?.subscriptionPlan?.description
      : selectedOffre?.subscriptionPlan?.descriptionEn ||
        selectedOffre?.subscriptionPlan?.description

  const isSubscription =
    selectedOffre?.subscriptionPlan?.type === 'subscription'

  const statusKey = selectedOffre?.status
  const statusLabel =
    statusKey === 'payer'
      ? t('status.active')
      : statusKey === 'expirer'
      ? t('status.expired')
      : t('status.pending')
  const statusColor =
    statusKey === 'payer'
      ? 'success'
      : statusKey === 'expirer'
      ? 'error'
      : 'default'

  const formatDate = (date, withTime = true) =>
    date
      ? moment(date)
          .tz('Europe/Paris')
          .format(withTime ? 'DD/MM/YYYY HH:mm' : 'DD/MM/YYYY')
      : '—'

  return (
    <Dialog
      open={Boolean(selectedOffre)}
      onClose={() => setSelectedOffre(null)}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      scroll={'paper'}
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : '20px',
          p: fullScreen ? 0 : 0,
          m: fullScreen ? 0 : undefined,
          maxHeight: fullScreen ? '100%' : '90vh',
          overflow: 'hidden',
          ...(fullScreen && {
            display: 'flex',
            flexDirection: 'column',
          }),
        },
      }}
    >
      <DialogTitle
        component="div"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 2.5 },
          flexShrink: 0,
          background: BRAND_GRADIENT,
          color: '#fff',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1.5, sm: 2 },
            flex: 1,
            minWidth: 0,
          }}
        >
          <Avatar
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.95)',
              color: '#667eea',
              width: { xs: 48, sm: 56 },
              height: { xs: 48, sm: 56 },
              flexShrink: 0,
              boxShadow: '0 4px 14px rgba(0, 0, 0, 0.15)',
            }}
          >
            <FontAwesomeIcon icon={faTag} fontSize={fullScreen ? 20 : 24} />
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant={fullScreen ? 'h6' : 'h5'}
              component="div"
              sx={{
                fontWeight: 700,
                mb: 0.5,
                wordBreak: 'break-word',
                lineHeight: 1.25,
              }}
            >
              {planName}
            </Typography>
            <Chip
              size="small"
              label={
                isSubscription
                  ? t('dashboard.subscriptionDetails')
                  : t('dashboard.oneShotOffer')
              }
              sx={{
                height: 24,
                fontWeight: 600,
                fontSize: '0.7rem',
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                color: '#fff',
                border: '1px solid rgba(255, 255, 255, 0.35)',
              }}
            />
          </Box>
        </Box>
        <IconButton
          onClick={() => setSelectedOffre(null)}
          sx={{
            color: '#fff',
            flexShrink: 0,
            bgcolor: 'rgba(255, 255, 255, 0.12)',
            '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.22)' },
          }}
          aria-label={t('profile.close')}
        >
          <FontAwesomeIcon icon={faXmark} />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          pt: '16px !important',
          px: { xs: 1, sm: 2 },
          py: { xs: 1, sm: 2 },
          flex: fullScreen ? 1 : undefined,
          overflow: 'auto',
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <StatIcon icon={faCalendarAlt} />
                <Typography
                  variant="caption"
                  sx={{
                    color: '#6b7280',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  {t('profile.date')}
                </Typography>
              </Box>
              <Typography
                variant="body1"
                sx={{ fontWeight: 700, color: '#1f2937', wordBreak: 'break-word' }}
              >
                {formatDate(selectedOffre?.createdAt)}
              </Typography>
            </StatCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StatCard>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <StatIcon icon={faClock} />
                <Typography
                  variant="caption"
                  sx={{
                    color: '#6b7280',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  {t('profile.dateExpire')}
                </Typography>
              </Box>
              <Typography
                variant="body1"
                sx={{ fontWeight: 700, color: '#1f2937', wordBreak: 'break-word' }}
              >
                {formatDate(selectedOffre?.endDate, false)}
              </Typography>
            </StatCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StatCard>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <StatIcon icon={faTag} />
                <Typography
                  variant="caption"
                  sx={{
                    color: '#6b7280',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  {t('payment.duration')}
                </Typography>
              </Box>
              <Typography variant="body1" sx={{ fontWeight: 700, color: '#1f2937' }}>
                {selectedOffre?.subscriptionPlan?.periodDays ?? '—'}{' '}
                <Typography component="span" variant="body2" sx={{ color: '#6b7280' }}>
                  {t('payment.days')}
                </Typography>
              </Typography>
            </StatCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StatCard>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <StatIcon icon={faCheck} />
                <Typography
                  variant="caption"
                  sx={{
                    color: '#6b7280',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  {t('profile.status')}
                </Typography>
              </Box>
              <StatusChip
                label={statusLabel}
                statuscolor={statusColor}
                size="small"
              />
            </StatCard>
          </Grid>

          <Grid item xs={12}>
            <SectionCard
              sx={{
                borderLeft: '4px solid #667eea',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between',
                  alignItems: { xs: 'flex-start', sm: 'flex-start' },
                  gap: 2,
                  mb: 2,
                }}
              >
                <Box>
                  <Typography
                    variant="overline"
                    sx={{ color: '#667eea', fontWeight: 700, letterSpacing: 1 }}
                  >
                    {t('payment.planDetails')}
                  </Typography>
                  <Typography
                    variant={fullScreen ? 'subtitle1' : 'h6'}
                    sx={{ fontWeight: 700, color: '#1f2937', mt: 0.5 }}
                  >
                    {planName}
                  </Typography>
                </Box>
                {selectedOffre?.subscriptionType !== 'trial' && (
                  <Box
                    sx={{
                      px: 2.5,
                      py: 1.5,
                      borderRadius: '12px',
                      background: BRAND_GRADIENT,
                      color: '#fff',
                      textAlign: { xs: 'left', sm: 'right' },
                      minWidth: { sm: 140 },
                      boxShadow: '0 4px 14px rgba(102, 126, 234, 0.35)',
                    }}
                  >
                    <Typography variant="caption" color="white" sx={{ opacity: 0.9, display: 'block' }}>
                      {t('payment.price')}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                      CHF {selectedOffre?.subscriptionPlan?.basePrice}
                    </Typography>
                  </Box>
                )}
              </Box>

              {planDescription && (
                <Typography
                  variant="body2"
                  sx={{
                    color: '#4b5563',
                    lineHeight: 1.7,
                    mb: 2,
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                    '& img': { maxWidth: '100%', height: 'auto', borderRadius: 8 },
                    '& p': { margin: '0 0 8px' },
                  }}
                  dangerouslySetInnerHTML={{ __html: planDescription }}
                />
              )}

              {selectedOffre?.subscriptionPlan?.features?.length > 0 && (
                <>
                  <Divider sx={{ my: 2, borderColor: 'rgba(102, 126, 234, 0.12)' }} />
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, color: '#374151', mb: 1.5 }}
                  >
                    {t('payment.features')}
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, 1fr)',
                      },
                      gap: 1,
                    }}
                  >
                    {selectedOffre.subscriptionPlan.features.map((feature, index) => (
                      <FeatureItem key={index}>
                        <FontAwesomeIcon
                          icon={faCircleCheck}
                          fontSize={16}
                          color="#667eea"
                          style={{ marginTop: 2, flexShrink: 0 }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ color: '#374151', fontWeight: 500, wordBreak: 'break-word' }}
                        >
                          {i18n.language === 'fr'
                            ? feature.name
                            : feature.nameEn || feature.name}
                        </Typography>
                      </FeatureItem>
                    ))}
                  </Box>
                </>
              )}
            </SectionCard>
          </Grid>

          <Grid item xs={12}>
            <SectionCard>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  color: '#1f2937',
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background:
                      'linear-gradient(135deg, rgba(118, 75, 162, 0.12), rgba(102, 126, 234, 0.12))',
                    color: '#764ba2',
                  }}
                >
                  <FontAwesomeIcon icon={faCreditCard} fontSize={13} />
                </Box>
                {t('dashboard.additionalInfo')}
              </Typography>
              <Grid container spacing={1.5}>
                {selectedOffre?.subscriptionType !== 'trial' && (
                  <Grid item xs={12} sm={6}>
                    <InfoRow>
                      <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600 }}>
                        {t('dashboard.paymentDate')}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#1f2937' }}>
                        {formatDate(selectedOffre?.paymentDate)}
                      </Typography>
                    </InfoRow>
                  </Grid>
                )}
                {selectedOffre?.autoRenew && (
                  <Grid item xs={12} sm={6}>
                    <InfoRow>
                      <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600 }}>
                        {t('dashboard.nextPaymentDate')}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#1f2937' }}>
                        {formatDate(selectedOffre?.nextPaymentDate)}
                      </Typography>
                    </InfoRow>
                  </Grid>
                )}
                {selectedOffre?.subscriptionType !== 'trial' && (
                  <Grid item xs={12} sm={6}>
                    <InfoRow>
                      <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600 }}>
                        {t('payment.paymentMethod')}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: '#1f2937', wordBreak: 'break-word' }}
                      >
                        {selectedOffre?.paymentMethod || '—'}
                      </Typography>
                    </InfoRow>
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <InfoRow>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FontAwesomeIcon icon={faRotate} fontSize={12} color="#6b7280" />
                      <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600 }}>
                        {t('dashboard.autoRenew')}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={
                        selectedOffre?.autoRenew ? t('button.yes') : t('button.no')
                      }
                      sx={{
                        alignSelf: 'flex-start',
                        fontWeight: 600,
                        bgcolor: selectedOffre?.autoRenew
                          ? 'rgba(76, 175, 80, 0.12)'
                          : 'rgba(107, 114, 128, 0.1)',
                        color: selectedOffre?.autoRenew ? '#2e7d32' : '#6b7280',
                      }}
                    />
                  </InfoRow>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InfoRow>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FontAwesomeIcon icon={faCoins} fontSize={12} color="#6b7280" />
                      <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600 }}>
                        {t('dashboard.credits')}
                      </Typography>
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#667eea' }}>
                      {selectedOffre?.credits ?? '—'}
                    </Typography>
                  </InfoRow>
                </Grid>
              </Grid>
            </SectionCard>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2, sm: 4 },
          py: { xs: 2, sm: 3 },
          gap: 2,
          justifyContent: 'center',
          backgroundColor: '#fff',
          borderTop: '1px solid rgba(229, 231, 235, 0.9)',
          flexDirection: { xs: 'column', sm: 'row' },
          flexShrink: 0,
          '& > *': {
            width: { xs: '100%', sm: 'auto' },
            m: 0,
          },
        }}
      >
        <OutlineButton
          onClick={() => setSelectedOffre(null)}
          fullWidth={fullScreen}
        >
          {t('profile.close')}
        </OutlineButton>
        {selectedOffre?.subscriptionType === 'subscription' && (
          <GradientButton
            onClick={() => navigate('/entreprises/offres')}
            fullWidth={fullScreen}
          >
            <FontAwesomeIcon icon={faCreditCard} style={{ marginRight: 8 }} />
            {t('payment.renewSubscription')}
          </GradientButton>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default DetailSubscription
